/**
 * DNH — Google OAuth Callback Page (/auth/google)
 *
 * The backend completes the OAuth code exchange and redirects here with:
 *   /auth/google?status=success&from=<encoded_path>
 *
 * This page just needs to:
 *  1. Read the status & from params
 *  2. Call checkAuth() to hydrate auth context (cookie was already set by backend)
 *  3. Show a welcome toast
 *  4. Navigate to `from`
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../components/dnh/AuthContext";
import { safeReturnPath } from "../utils/safeReturnPath";

export default function GoogleAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, authStatus, authError, checkAuth } = useAuth();
  const [checked, setChecked] = useState(false);
  const status = searchParams.get("status"), error = searchParams.get("error");
  const destination = safeReturnPath(searchParams.get("from"));

  useEffect(() => {
    if (error || status !== "success") {
      const msgs = {
        oauth_denied:          "Sign-in was cancelled.",
        token_rejected:        "Google rejected the sign-in token. Please try again.",
        token_exchange_failed: "Could not reach Google's servers. Please try again.",
        missing_code:          "Invalid OAuth response. Please try again.",
        no_email:              "Your Google account has no verified email.",
      };
      toast.error(msgs[error] || "Sign-in failed. Please try again.");
      navigate("/signin", { replace: true });
      return;
    }

    // The backend already set the cookie. Only a live effect may complete this
    // check: StrictMode cleanup cancels the first effect before its microtask.
    let active = true;
    setChecked(false);
    Promise.resolve().then(async () => {
      if (!active) return;
      await checkAuth({ force: true });
      if (active) setChecked(true);
    });
    return () => { active = false; };
  }, [checkAuth, navigate, status, error]);

  useEffect(() => {
    if (!checked || error || status !== "success" || authError) return;
    if (user && authStatus === "authenticated") {
      const name = user.name?.split(" ")[0];
      toast.success(name ? `Welcome back, ${name}!` : "You're signed in.");
      navigate(destination, { replace: true });
    } else if (authStatus === "anonymous") {
      // Only an actual rejected session is a failed sign-in. Transport errors
      // stay on this page; a successful retry can complete the same callback.
      toast.error("Sign-in could not be confirmed. Please sign in again.");
      navigate(`/signin?from=${encodeURIComponent(destination)}`, { replace: true });
    }
  }, [checked, user, authStatus, authError, destination, navigate, status, error]);

  return (
    <div
      className="min-h-screen bg-[var(--paper)] flex items-center justify-center"
      data-testid="google-auth-callback"
    >
      <div className="text-center">
        <div
          className="w-6 h-6 border-2 border-[var(--hairline)] border-t-[var(--crimson)] rounded-full animate-spin mx-auto mb-4"
          aria-hidden
        />
        <p data-testid="google-auth-callback-status" className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--muted)]">
          {authError ? "Waiting to confirm your session…" : "Signing you in…"}
        </p>
      </div>
    </div>
  );
}
